import React, { useEffect, useRef, useState } from "react";

import { Route, Routes } from "react-router-dom";
import { ProtectedAdminRoute, ProtectedRoute } from "./hooks/PrivateRoute";
import { Game } from "./pages/Game/game";
import { Loader } from "./components/loader/loader";
import { MainLayout } from "./layouts/main.layout";
import { GameLayout } from "./layouts/game.layout";
import Rules, { accordionData } from "./pages/Rules/Rules";

import {
  ProfileDeposit,
  ProfileWithdrawal,
  ProfilePaymentHistory,
} from "./components/Profile/Payments/";
import Login from "./pages/Login/Login";
import Registration from "./pages/Registration/Registration";
import AuthRoute from "./hooks/authRoute";
import {
  ProfileContacts,
  ProfilePersonalInfoWrapper,
  ProfileVerification,
} from "./components/Profile/PersonalInfo";
import ProfileSecurity from "./components/Profile/ProfileSecurity/ProfileSecurity";
// import { Analytics } from "@vercel/analytics/react";
// import { SpeedInsights } from "@vercel/speed-insights/react";
import ProfileActiveBonuses, {
  ProfileBonusesWrapper,
} from "./components/Profile/ProfileActiveBonuses/ActiveBonus/ProfileActiveBonuses";
import ProfileAvailableBonuses from "./components/Profile/ProfileActiveBonuses/AvailableBonus/ProfileAvailableBonuses";
import ProfileHistoryBonuses from "./components/Profile/ProfileActiveBonuses/HistoryBonus/HistoryBonus";
import { useAppDispatch, useAppSelector } from "./store/hooks/store.hooks";
import { fetchBonuses } from "./store/models/bonuses/bonuses.thunk";
import { useMediaQuery } from "react-responsive";
import { ThemeProvider, createTheme } from "@mui/material";
import { NotFound } from "./components/404/NotFound";
import { YandexConfirmation } from "./pages/Login/yandex/YandexConfirmation";
import { SteamConfirmation } from "./pages/Login/steam/SteamConfirmation";
import { Slots } from "./pages/Slots/Slots";
import { Live } from "./pages/Live/Live";
import { Crash } from "./pages/Crash/Crash";
import { ScrollResetAndTopMargin } from "./layouts/scroll.reset.top.margin";
import { GameWrapper } from "./components/Game/GameWrapper/GameWrapper";
import { RootState } from "./store/rootStore";
import {
  IBigWin,
  INewBalance,
} from "./store/models/slots/typing/slot.interface";
import { addBigWin, setBigWins } from "./store/models/slots/slot.slice";
import { useSocket } from "./hooks/SocketContext";
import { verifyAuthUser } from "./store/models/auth/auth.thunk";
import { updateBalance } from "./store/models/user/user.slice";
import Redirector from "./hooks/Redirector";

const RequestPassword = React.lazy(
  () => import("./pages/Resetpassword/RequestPassword")
);
const Partner = React.lazy(() => import("./pages/Partner/partner"));
const Club = React.lazy(() => import("./pages/Club/Club"));
const Coins = React.lazy(() => import("./pages/Coins/Coins"));

const Profile = React.lazy(() => import("./pages/Profile/Profile"));
const Lobby = React.lazy(() => import("./pages/Lobby/Lobby"));
const Bonuses = React.lazy(() => import("./pages/Bonuses/Bonuses"));
const BonusId = React.lazy(() => import("./pages/Bonuses/BonusID/bonus.id"));
const ProfileLayout = React.lazy(() => import("./layouts/profile.layout"));
// const Payments = React.lazy(() => import("./pages/Payments/Payments"));
const PaymentsSeccessful = React.lazy(
  () => import("./pages/Payments/PaymentsSeccessful/PaymentsSeccessful")
);
const PaymentsFailure = React.lazy(
  () => import("./pages/Payments/PaymentsFailure/PaymentsFailure")
);
const Main = React.lazy(() => import("./pages/Main/main"));

// const RequestPassword = "./pages/Resetpassword/RequestPassword";

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#df0043",
    },
    secondary: {
      main: "#f50057",
    },
    background: {
      default: "#131a2f",
      paper: "#1b2740",
    },
    text: {
      primary: "#f8f8f8",
      secondary: "#5e5e72",
      disabled: "#5e5e72",
    },
  },
  typography: {
    fontSize: 12,
    fontWeightRegular: 500,
    fontWeightBold: 700,
    htmlFontSize: 16,
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      defaultProps: {
        sx: {
          border: "none",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        sx: {
          border: "none",
        },
      },
    },
    MuiCard: {
      defaultProps: {
        sx: {
          border: "none",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        sx: {
          border: "none",
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        sx: {
          border: "none",
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        sx: {
          border: "none",
        },
      },
    },
  },
  spacing: [2, 4, 8, 12, 16, 25, 32, 36],
});

const App = () => {
  // const { bonuses } = useAppSelector(bonusesListSelector);
  const dispatch = useAppDispatch();

  const { socket, isConnected } = useSocket();
  const channelRef = useRef<BroadcastChannel | null>(null);
  // const channel = new BroadcastChannel("bigwin_channel");

  useEffect(() => {
    if (!isConnected || !socket) return;

    // Створюємо BroadcastChannel один раз
    if (!channelRef.current) {
      channelRef.current = new BroadcastChannel("bigwin_channel");
    }

    const handleBigWins = (wins: IBigWin[]) => dispatch(setBigWins(wins));
    const handleNewBigWin = (win: IBigWin) => {
      dispatch(addBigWin(win));
      channelRef.current?.postMessage(win);
    };
    const handleBalanceUpdate = (data: INewBalance) =>
      dispatch(updateBalance(data));

    socket.on("latestBigWins", handleBigWins);
    socket.on("newBigWin", handleNewBigWin);
    socket.on("user-balance-update", handleBalanceUpdate);

    channelRef.current.onmessage = (event) => dispatch(addBigWin(event.data));

    return () => {
      socket.off("latestBigWins", handleBigWins);
      socket.off("newBigWin", handleNewBigWin);
      socket.off("user-balance-update", handleBalanceUpdate);

      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [socket, isConnected, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit("ping");
      }
    }, 40000);

    return () => clearInterval(interval);
  }, [socket]);

  useEffect(() => {
    async function fetch() {
      await dispatch(verifyAuthUser());
    }
    fetch();
  }, [dispatch]);
  return (
    <React.Fragment>
      <ThemeProvider theme={darkTheme}>
        <React.Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Main />} />
              <Route
                path="profile"
                //element={<ProfileLayout />
                element={<ProtectedRoute component={ProfileLayout} redirect />}
              >
                <Route index element={<Profile />} />
                <Route path="personal-info">
                  <Route
                    index
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfilePersonalInfoWrapper />
                      </ScrollResetAndTopMargin>
                    }
                  />
                  <Route
                    path="contacts"
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileContacts />
                      </ScrollResetAndTopMargin>
                    }
                  />
                  <Route
                    path="verification"
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileVerification />
                      </ScrollResetAndTopMargin>
                    }
                  />
                </Route>
                <Route path="payments">
                  <Route
                    index
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileDeposit />
                      </ScrollResetAndTopMargin>
                    }
                  />
                  <Route
                    path="withdrawal"
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileWithdrawal />
                      </ScrollResetAndTopMargin>
                    }
                  />
                  <Route
                    path="payment-history"
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfilePaymentHistory />
                      </ScrollResetAndTopMargin>
                    }
                  />
                </Route>

                <Route path="bonuses">
                  <Route
                    index
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileBonusesWrapper />
                      </ScrollResetAndTopMargin>
                    }
                  />
                  <Route
                    path="available"
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileAvailableBonuses />
                      </ScrollResetAndTopMargin>
                    }
                  />
                  <Route
                    path="history"
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileHistoryBonuses />
                      </ScrollResetAndTopMargin>
                    }
                  />
                </Route>
                <Route path="security">
                  <Route
                    index
                    element={
                      <ScrollResetAndTopMargin>
                        <ProfileSecurity />
                      </ScrollResetAndTopMargin>
                    }
                  />
                </Route>
              </Route>
              <Route
                path="partner"
                element={<ProtectedRoute component={Partner} />}
              ></Route>
              <Route
                path="coins"
                element={<ProtectedAdminRoute component={Coins} />}
              />
              {/* <Route
                path="club"
                // element={<ProtectedAdminRoute component={Club} />}
                element={<Club />}
              /> */}
              <Route path="payments">
                {/* <Route
                    path="successful"
                    element={<ProtectedRoute component={PaymentsSeccessful} />}
                  /> */}
                <Route
                  path="failure"
                  element={<ProtectedRoute component={PaymentsFailure} />}
                />
              </Route>
              {accordionData.map((el) => (
                <Route key={el.id} path={el.pathname} element={<Rules />} />
              ))}

              <Route path="login" element={<AuthRoute component={Login} />} />
              <Route
                path="login/yandex/callback"
                element={<YandexConfirmation />}
              />
              <Route
                path="login/steam/callback"
                element={<SteamConfirmation />}
              />
              <Route
                path="registration"
                element={<AuthRoute component={Registration} />}
              />
              <Route path="/slots/:game" element={<GameWrapper />}>
                <Route index element={<Game />} />
              </Route>
              <Route path="/lobby">
                <Route index element={<Lobby />} />
                <Route path="/lobby/slots" element={<Slots />} />
                <Route path="/lobby/live" element={<Live />} />
                <Route path="/lobby/crash" element={<Crash />} />
              </Route>
              <Route path="/bonuses">
                <Route index element={<Bonuses />} />
                <Route path=":bonusId" element={<BonusId />} />
              </Route>
              <Route
                path="/authorization/restore/:token"
                element={<AuthRoute component={RequestPassword} />}
              />
              <Route path="/redirector.html" element={<Redirector />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </React.Suspense>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default App;
